import React, { useState, useEffect, useContext } from 'react';
import {
  getAuth,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut
} from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { AppContext } from '../AppContext'; // adjust path if your context lives elsewhere


const Login = ({ isMFAEnabled, setIsMFAEnabled, setShowMFAPrompt, deviceToken, onSwitchToSignup, onPostLoginSetupCompleted }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const auth = getAuth();
  const firestore = getFirestore();

  const { setUser, setProfileImage, setCredits, setIsLoggedIn } = useContext(AppContext);

  const [showVerifyBanner, setShowVerifyBanner] = useState(false);
  const [bannerEmail, setBannerEmail] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  // Optional deep-link passthrough (kept from SPNDAT behavior)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const source = searchParams.get('source');
    const path = location.pathname;

    if (source === 'app' && path.includes('/mydat/profile/')) {
      const username = path.split('/mydat/profile/')[1];
      if (username) {
        window.location.href = `myapp://profile/${username}`;
      }
    }
  }, [location]);

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email to reset your password');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      alert('Please check your email to reset your password');
    } catch (firebaseError) {
      console.error('Password reset error:', firebaseError.message);
      setError(firebaseError.message);
    }
  };

  const CustomButton = ({ onClick, title, textStyle, buttonStyle }) => (
    <button onClick={onClick} style={{ ...styles.button, ...buttonStyle }}>
      <span style={{ ...styles.buttonText, ...textStyle }}>{title}</span>
    </button>
  );

  const postLoginSetup = async (user) => {
    if (!user) {
      console.error('No user is logged in');
      setError('No user is logged in');
      return;
    }

    try {
      const userRef = doc(firestore, 'users', user.uid);
      const snap = await getDoc(userRef);

      if (snap.exists()) {
        const userData = snap.data();

        if (deviceToken) {
          await updateDoc(userRef, { deviceToken });
        }

        setUser(userData);
        setProfileImage(userData.profileImage || '');
        setCredits(userData.credits || 0);
        setIsLoggedIn(true);

        if (typeof onPostLoginSetupCompleted === 'function') {
          onPostLoginSetupCompleted(userData);
        }

        // Street Flavor: send to the shop after login
        navigate('/shop');
      } else {
        console.error('No user data found');
        setError('No user data found');
      }
    } catch (firebaseError) {
      console.error('postLoginSetup error:', firebaseError.message);
      setError(firebaseError.message);
    }
  };

  const handleLogin = async () => {
    setError('');
    setShowVerifyBanner(false);

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      // 1) sign in
      const { user } = await signInWithEmailAndPassword(auth, email, password);

      // 2) reload profile to get latest verification state
      await user.reload();

      // 3) if not verified, send link & show banner, then sign out
      if (!user.emailVerified) {
        await sendEmailVerification(user, {
          // Using Street Flavor site for the return link
          url: `${window.location.origin}/login`
        });
        await signOut(auth);
        setBannerEmail(user.email || email);
        setShowVerifyBanner(true);
        return;
      }

      // 4) verified → continue
      await postLoginSetup(user);
    } catch (firebaseError) {
      console.error('Login error:', firebaseError.message);
      setError(firebaseError.message);
    }
  };

  // If the router pushed state with a verify banner request (like SPNDAT does)
  const routedState = location.state || {};

  return (
    <>
      <div style={styles.pageWrap}>
        <div style={styles.form}>
<h1 style={styles.header}>Sign in to your shared account</h1>
<p style={styles.crossBrandNote}>
  One account for <strong>SPNDAT</strong>, <strong>THEDATCHAIN</strong>, and <strong>Street Flavor</strong>.
  If you’ve signed up on any of them, use the same email and password here.
</p>


          {routedState?.showVerifyBanner && (
            <div style={styles.verifyBanner}>
              Please verify <strong>{routedState?.email}</strong> before logging in.
            </div>
          )}

          {showVerifyBanner && (
            <div style={styles.verifyBanner}>
              Please verify <strong>{bannerEmail}</strong> before logging in.
              <br />
              A fresh link has been sent to your inbox.
            </div>
          )}

          {error ? <p style={styles.error}>{error}</p> : null}

          <input
            style={styles.input}
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <input
            style={styles.input}
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <CustomButton
            title="Login"
            onClick={handleLogin}
            textStyle={{ color: '#000' }}
            buttonStyle={styles.primaryButton}
          />

          <CustomButton
title="Create account — works across SPNDAT / THEDATCHAIN / Street Flavor"

            onClick={() => navigate('/signup')}
            textStyle={{ color: '#fdca05', fontSize: '1rem' }}
            buttonStyle={styles.ghostButton}
          />

          <CustomButton
            title="Forgot Password?"
            onClick={handlePasswordReset}
            textStyle={{ color: '#fdca05' }}
            buttonStyle={styles.ghostButton}
          />

          <CustomButton
            title="Privacy Policy"
            onClick={() => navigate('/privacy-policy')}
            textStyle={{ color: '#fdca05' }}
            buttonStyle={styles.ghostButton}
          />


        </div>
      </div>
    </> 
  );
};

const styles = {
  pageWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: '100vh',
    backgroundColor: '#000',
    paddingTop: 60
  },
  form: {
    width: '90%',
    maxWidth: 420,
    backgroundColor: '#000',
    padding: 20,
    borderRadius: 10,
    border: '1px solid rgba(128, 128, 78, 0.25)',
    boxSizing: 'border-box',
    margin: '0 auto'
  },
  header: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: 16,
    textAlign: 'center',
    color: '#fff'
  },
  input: {
    width: '100%',
    maxWidth: '100%',
    height: 44,
    border: '1px solid #333',
    margin: '0 auto 14px',
    padding: '8px 10px',
    fontSize: '1rem',
    color: '#fff',
    backgroundColor: 'rgba(255,255,255,0.08)',
    borderRadius: 6,
    outline: 'none',
    display: 'block',
    boxSizing: 'border-box'
  },
  error: {
    color: '#ff6b6b',
    marginBottom: 12,
    fontSize: '0.95rem'
  },
  button: {
    padding: 12,
    borderRadius: 6,
    textAlign: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    width: '100%',
    boxSizing: 'border-box',
    cursor: 'pointer',
    border: '1px solid #fdca05',
    transition: 'transform 120ms ease'
  },
  crossBrandNote: {
  backgroundColor: 'rgba(128, 128, 78, 0.08)',
  border: '1px solid #fdca05',
  color: '#fdca05',
  padding: '10px 12px',
  borderRadius: 6,
  textAlign: 'center',
  marginBottom: 14,
  fontSize: '0.95rem',
  fontWeight: 500
},

  buttonText: {
    fontSize: '1rem',
    color: '#fff'
  },
  
  primaryButton: {
    backgroundColor: '#fdca05',
    borderColor: '#fdca05',
    marginBottom: 14
  },
  ghostButton: {
    backgroundColor: 'transparent',
    borderColor: '#fdca05',
    marginBottom: 12
  },
  verifyBanner: {
    backgroundColor: 'rgba(128, 128, 78, 0.08)',
    border: '1px solid #fdca05',
    color: '#fdca05',
    padding: '12px 16px',
    borderRadius: 6,
    textAlign: 'center',
    marginBottom: 14,
    fontSize: '0.95rem',
    fontWeight: 500
  },
  storeBadgeWrapTop: {
    position: 'relative',
    display: 'flex',
    alignSelf: 'center',
    justifyContent: 'center',
    margin: '8px auto 0',
    cursor: 'pointer'
  },
  storeBadgeWrapBottom: {
    position: 'relative',
    display: 'flex',
    alignSelf: 'center',
    justifyContent: 'center',
    margin: '12px auto 0',
    cursor: 'pointer'
  },
  storeBadgeImg: {
    width: 150,
    height: 50,
    objectFit: 'cover',
    backgroundColor: 'transparent'
  }
};


export default Login;
